<!-- Preview Mode -->
<div *ngIf="mode === 'preview'" [ngStyle]="getPreviewStyles()" class="prose max-w-none p-6 rounded-lg">
  <div *ngIf="getHeading()" class="mb-6">
    <h2 [style.color]="getHeadingColor()" [ngClass]="getTitlePositionClass()" class="text-3xl font-bold">{{ getHeading() }}</h2>
  </div>

  <!-- No Projects Selected State -->
  <div *ngIf="!hasSelectedProjects()" class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
    <h3 class="mt-2 text-sm font-medium text-gray-900">Geen projecten geselecteerd</h3>
    <p class="mt-1 text-sm text-gray-500">Selecteer eerst projecten in de sidebar om ze hier te tonen.</p>
  </div>

  <!-- Loading State -->
  <div *ngIf="hasSelectedProjects() && loading" class="flex justify-center items-center py-12">
    <div class="text-gray-400">Projecten laden...</div>
  </div>

  <!-- Projects Grid View -->
  <div *ngIf="hasSelectedProjects() && !loading && getViewType() === 'grid'" [ngClass]="getGridColumnsClass()" class="grid gap-6">
    <div *ngFor="let project of filteredProjects" class="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow relative group">
      <!-- Remove Button -->
      <button
        *ngIf="project.id"
        (click)="onRemoveProject(project.id, $event)"
        class="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-red-500 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
        title="Project verwijderen">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div *ngIf="getProjectImage(project)" class="aspect-w-16 aspect-h-9 bg-gray-200">
        <img [src]="getProjectImage(project)" [alt]="getProjectTitle(project)" class="w-full h-48 object-cover">
      </div>
      <div class="p-4">
        <h3 [style.color]="getTextColor()" class="text-lg font-semibold mb-2">{{ getProjectTitle(project) }}</h3>
        <div *ngIf="getShowCategory() && project.categories && project.categories.length > 0" class="flex flex-wrap gap-1 mb-2">
          <span *ngFor="let cat of project.categories" class="px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded">{{ cat }}</span>
        </div>
        <p *ngIf="getShowDescription()" [style.color]="getTextColor()" class="text-sm mb-3 line-clamp-2 opacity-80">
          {{ getProjectDescription(project) }}
        </p>
        <div *ngIf="project.tags && project.tags.length > 0" class="flex flex-wrap gap-1">
          <span *ngFor="let tag of project.tags" class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
            {{ tag }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Projects List View -->
  <div *ngIf="hasSelectedProjects() && !loading && getViewType() === 'list'" class="space-y-4">
    <div *ngFor="let project of filteredProjects" class="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex relative group">
      <!-- Remove Button -->
      <button
        *ngIf="project.id"
        (click)="onRemoveProject(project.id, $event)"
        class="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-red-500 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
        title="Project verwijderen">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div *ngIf="getProjectImage(project)" class="w-48 h-36 flex-shrink-0">
        <img [src]="getProjectImage(project)" [alt]="getProjectTitle(project)" class="w-full h-full object-cover">
      </div>
      <div class="flex-1 p-4">
        <h3 [style.color]="getTextColor()" class="text-lg font-semibold mb-2">{{ getProjectTitle(project) }}</h3>
        <div *ngIf="getShowCategory() && project.categories && project.categories.length > 0" class="flex flex-wrap gap-1 mb-2">
          <span *ngFor="let cat of project.categories" class="px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded">{{ cat }}</span>
        </div>
        <p *ngIf="getShowDescription()" [style.color]="getTextColor()" class="text-sm opacity-80">
          {{ getProjectDescription(project) }}
        </p>
        <div *ngIf="project.tags && project.tags.length > 0" class="flex flex-wrap gap-1 mt-3">
          <span *ngFor="let tag of project.tags" class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
            {{ tag }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Projects Cards View -->
  <div *ngIf="hasSelectedProjects() && !loading && getViewType() === 'cards'" [ngClass]="getGridColumnsClass()" class="grid gap-6">
    <div *ngFor="let project of filteredProjects" class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative group">
      <!-- Remove Button -->
      <button
        *ngIf="project.id"
        (click)="onRemoveProject(project.id, $event)"
        class="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/90 text-gray-600 opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white transition-all"
        title="Project verwijderen">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div *ngIf="getProjectImage(project)" class="relative h-56 bg-gray-200 overflow-hidden">
        <img [src]="getProjectImage(project)" [alt]="getProjectTitle(project)" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105">
        <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div class="absolute bottom-4 left-4 right-4">
          <h3 class="text-white text-xl font-bold drop-shadow-lg">{{ getProjectTitle(project) }}</h3>
        </div>
      </div>
      <div class="p-5">
        <div *ngIf="getShowCategory() && project.categories && project.categories.length > 0" class="flex flex-wrap gap-1 mb-3">
          <span *ngFor="let cat of project.categories" class="px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded">{{ cat }}</span>
        </div>
        <p *ngIf="getShowDescription()" [style.color]="getTextColor()" class="text-sm line-clamp-3 opacity-80">
          {{ getProjectDescription(project) }}
        </p>
        <div *ngIf="project.tags && project.tags.length > 0" class="flex flex-wrap gap-2 mt-4">
          <span *ngFor="let tag of project.tags" class="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-full">
            {{ tag }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State (when projects are selected but none found) -->
  <div *ngIf="hasSelectedProjects() && !loading && filteredProjects.length === 0" class="text-center py-12">
    <p class="text-gray-400">Geen projecten gevonden</p>
  </div>
</div>

<!-- Edit Mode -->
<div *ngIf="mode === 'edit'">
  <p class="text-sm text-gray-400">Gebruik de sidebar om de weergave en filters aan te passen.</p>
</div>
